import { chartNum, chartLabel } from '@/lib/recharts-format';
import React, { useState, useEffect, useCallback, useRef, type SVGProps } from 'react';
import { 
  Database, 
  Table2, 
  HardDrive, 
  Users, 
  Activity,
  RefreshCw,
  Search,
  ChevronRight,
  Terminal,
  Play,
  Trash2,
  Edit,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  Info,
  BarChart3,
  PieChart,
  TrendingUp,
  Loader2,
  Copy,
  Download,
  Brain,
  FileText,
  Globe,
  Zap,
  Hash,
  Orbit
} from 'lucide-react';
import { HATab } from '@/components/database/HATab';
import { ObservabilityTab } from '@/components/database/ObservabilityTab';
import { BackupTab } from '@/components/database/BackupTab';
import { RestorePanel } from '@/components/database/RestorePanel';
import { AnomaliesBanner } from '@/components/database/AnomaliesBanner';
import { Cloud } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { ScrollArea } from '@easyflow/ui-master/components/ui/scroll-area';
import { Textarea } from '@easyflow/ui-master/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@easyflow/ui-master/components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line, Legend, ScatterChart, Scatter, ZAxis, AreaChart, Area } from 'recharts';
import api from '@/lib/api';

interface DbStats {
  database_size: string;
  database_size_bytes: number;
  table_count: number;
  active_connections: number;
  max_connections: number;
  uptime: string;
  version: string;
}

interface TableInfo {
  table_name: string;
  row_count: number;
  table_size: string;
  index_size: string;
  total_size: string;
  total_size_bytes: number;
  owner: string;
}

interface TenantStorage {
  tenant_id: string;
  tenant_name: string;
  total_rows: number;
  tables: { table_name: string; row_count: number }[];
}

interface ColumnInfo {
  column_name: string;
  data_type: string;
  is_nullable: string;
  column_default: string | null;
  max_length: number | null;
}

interface TableSchema {
  columns: ColumnInfo[];
  primary_keys: string[];
  foreign_keys: { column_name: string; foreign_table: string; foreign_column: string }[];
  indexes: { index_name: string; column_name: string; is_unique: boolean }[];
}

interface VectorStats {
  totals: {
    chunks: number;
    documents: number;
    urls: number;
    total_tokens: number;
    avg_tokens: number;
    embedding_coverage: number;
  };
  source_types: { source_type: string; count: number }[];
  timeline: { day: string; count: number }[];
  per_tenant: { tenant_id: string; tenant_name: string; chunk_count: number; doc_count: number; url_count: number; total_tokens: number }[];
  per_agent: { agent_id: string; tenant_id: string; tenant_name: string; agent_name: string; agent_type: string; chunk_count: number; doc_count: number; url_count: number; avg_tokens: number; total_tokens: number }[];
}

interface EmbeddingPoint {
  x: number;
  y: number;
  z: number;
  agent_name: string;
  agent_type: string;
  tenant_name: string;
  source_name: string;
  content_preview: string;
  token_count: number;
  provider: 'openai' | 'gemini';
  dimensions: number;
}

interface ProviderMapGroup {
  provider: 'openai' | 'gemini';
  dimensions: number;
  points: EmbeddingPoint[];
  stats: { total_in_db: number; sampled: number; with_embedding: number };
  error?: string;
}

interface EmbeddingMapData {
  providers: ProviderMapGroup[];
  total_in_db: number;
  total_with_embedding: number;
}

interface DbStatsRaw {
  database_size_formatted?: string;
  database_size?: number;
  table_count?: number;
  active_connections?: number;
  uptime?: string;
  version?: string;
}

interface TableInfoRaw {
  table_name: string;
  row_count_estimate?: number;
  table_size_formatted?: string;
  indexes_size_formatted?: string;
  total_size_formatted?: string;
  total_size?: number;
  owner?: string;
}

interface TableSchemaRaw {
  columns?: { name: string; type: string; nullable?: boolean; default_value?: string | null; max_length?: number | null }[];
  primary_keys?: string[];
  foreign_keys?: { column: string; references_table: string; references_column: string }[];
  indexes?: { name: string; columns?: string[]; is_unique?: boolean }[];
}

interface TableDataResponse {
  rows?: Record<string, unknown>[];
  pagination?: { total?: number };
}

interface PlotlyLike {
  react: (el: HTMLElement, traces: unknown[], layout: unknown, config: unknown) => void;
  newPlot: (el: HTMLElement, traces: unknown[], layout: unknown, config: unknown) => void;
}
type PlotlyModuleShape = { default?: PlotlyLike } & PlotlyLike;

interface ScatterShapeProps {
  cx?: number;
  cy?: number;
  fill?: string;
  payload?: { total_tokens?: number };
}

function extractApiError(error: unknown, fallback: string): string {
  if (error && typeof error === 'object') {
    const resp = (error as { response?: { data?: { error?: unknown } } }).response;
    if (typeof resp?.data?.error === 'string') return resp.data.error;
    const msg = (error as { message?: unknown }).message;
    if (typeof msg === 'string' && msg) return msg;
  }
  return fallback;
}

const COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6'];
const AGENT_TYPE_COLORS: Record<string, string> = { voice: '#3b82f6', chatbot: '#10b981', elevenlabs: '#8b5cf6', other: '#f59e0b' };
const SOURCE_TYPE_COLORS: Record<string, string> = { document: '#3b82f6', url: '#10b981', tenant_context: '#f59e0b' };

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

export default function DatabaseManager() {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [restoreFocusRequest, setRestoreFocusRequest] = useState(0);
  const openRestoreVerifications = useCallback(() => {
    setRestoreFocusRequest((request) => request + 1);
    setActiveTab('backup');
  }, []);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DbStats | null>(null);
  const [tables, setTables] = useState<TableInfo[]>([]);
  const [tenantStorage, setTenantStorage] = useState<TenantStorage[]>([]);
  const [selectedTable, setSelectedTable] = useState<string | null>(null);
  const [tableSchema, setTableSchema] = useState<TableSchema | null>(null);
  const [tableData, setTableData] = useState<Record<string, unknown>[]>([]);
  const [tableDataTotal, setTableDataTotal] = useState(0);
  const [tableDataPage, setTableDataPage] = useState(1);
  const [tableDataLimit] = useState(50);
  const [sqlQuery, setSqlQuery] = useState('SELECT * FROM tenants LIMIT 10;');
  const [queryResult, setQueryResult] = useState<Record<string, unknown>[] | null>(null);
  const [queryColumns, setQueryColumns] = useState<string[]>([]);
  const [queryError, setQueryError] = useState<string | null>(null);
  const [queryLoading, setQueryLoading] = useState(false);
  const [searchTables, setSearchTables] = useState('');
  const [editingRow, setEditingRow] = useState<Record<string, unknown> | null>(null);
  const [editingValues, setEditingValues] = useState<Record<string, unknown>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{ table: string; id: string } | null>(null);
  const [vectorStats, setVectorStats] = useState<VectorStats | null>(null);
  const [vectorLoading, setVectorLoading] = useState(false);
  const [embeddingMap, setEmbeddingMap] = useState<EmbeddingMapData | null>(null);
  const [embeddingLoading, setEmbeddingLoading] = useState(false);
  const [embeddingError, setEmbeddingError] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<'openai' | 'gemini'>('openai');
  const plotlyContainerRef = useRef<HTMLDivElement>(null);
  const [vectorSearch, setVectorSearch] = useState('');

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get<DbStatsRaw>('/master/database/stats');
      if (response.data) {
        const raw = response.data;
        setStats({
          database_size: raw.database_size_formatted || '-',
          database_size_bytes: raw.database_size || 0,
          table_count: raw.table_count || 0,
          active_connections: raw.active_connections || 0,
          max_connections: 100,
          uptime: raw.uptime || '-',
          version: raw.version || '-'
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchTables = useCallback(async () => {
    try {
      const response = await api.get<TableInfoRaw[]>('/master/database/tables');
      if (response.data && Array.isArray(response.data)) {
        setTables(response.data.map((t) => ({
          table_name: t.table_name,
          row_count: t.row_count_estimate ?? 0,
          table_size: t.table_size_formatted || '-',
          index_size: t.indexes_size_formatted || '-',
          total_size: t.total_size_formatted || '-',
          total_size_bytes: t.total_size ?? 0,
          owner: t.owner || 'unknown'
        })));
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  }, []);

  const fetchTenantStorage = useCallback(async () => {
    try {
      const response = await api.get<TenantStorage[]>('/master/database/tenant-storage');
      if (response.data && Array.isArray(response.data)) {
        setTenantStorage(response.data);
      }
    } catch (error) {
      console.error('Error fetching tenant storage:', error);
    }
  }, []);

  const fetchVectorStats = useCallback(async () => {
    setVectorLoading(true);
    try {
      const response = await api.get<VectorStats>('/master/database/vector-stats');
      if (response.data) {
        setVectorStats(response.data);
      }
    } catch (error) {
      console.error('Error fetching vector stats:', error);
    } finally {
      setVectorLoading(false);
    }
  }, []);

  const fetchEmbeddingMap = useCallback(async () => {
    setEmbeddingLoading(true);
    setEmbeddingError(null);
    try {
      const response = await api.get<EmbeddingMapData>('/master/database/vector-embedding-map');
      if (response.data) {
        const data = response.data;
        setEmbeddingMap(data);
        // Auto-seleziona il primo provider disponibile con dati
        const firstWithPoints = data.providers.find(p => p.points.length > 0);
        if (firstWithPoints) setSelectedProvider(firstWithPoints.provider);
        else if (data.providers[0]) setSelectedProvider(data.providers[0].provider);
      }
    } catch (error) {
      console.error('Error fetching embedding map:', error);
      setEmbeddingError('Errore nel caricamento della mappa embedding');
    } finally {
      setEmbeddingLoading(false);
    }
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchStats(), fetchTables(), fetchTenantStorage()]);
    setLoading(false);
  }, [fetchStats, fetchTables, fetchTenantStorage]);

  useEffect(() => {
    void fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (!embeddingMap || !plotlyContainerRef.current) return;
    const group = embeddingMap.providers.find(p => p.provider === selectedProvider);
    if (!group || group.points.length === 0) return;

    const renderPlotly = async () => {
      try {
        const PlotlyModule = (await import(/* @vite-ignore */ 'plotly.js/dist/plotly-gl3d.min' + '.js')) as PlotlyModuleShape;
        const PlotlyLib = PlotlyModule.default || PlotlyModule;

        const agentTypes = [...new Set(group.points.map(p => p.agent_type))];
        const traces = agentTypes.map(type => {
          const pts = group.points.filter(p => p.agent_type === type);
          return {
            x: pts.map(p => p.x),
            y: pts.map(p => p.y),
            z: pts.map(p => p.z),
            mode: 'markers' as const,
            type: 'scatter3d' as const,
            name: type.charAt(0).toUpperCase() + type.slice(1),
            marker: {
              size: pts.map(p => Math.max(3, Math.min(12, p.token_count / 200))),
              color: AGENT_TYPE_COLORS[type] || '#f59e0b',
              opacity: 0.8,
            },
            text: pts.map(p => `${p.agent_name}<br>${p.tenant_name}<br>${p.source_name}<br>Token: ${p.token_count}<br>${p.content_preview?.substring(0, 80)}...`),
            hoverinfo: 'text' as const,
          };
        });

        const providerLabel = selectedProvider === 'openai' ? 'OpenAI' : 'Gemini';
        const layout = {
          title: { text: `${providerLabel} — PCA ${group.dimensions}D→3D`, font: { size: 13 }, x: 0.5 },
          scene: {
            xaxis: { title: 'PC1', gridcolor: '#e5e7eb', zerolinecolor: '#d1d5db' },
            yaxis: { title: 'PC2', gridcolor: '#e5e7eb', zerolinecolor: '#d1d5db' },
            zaxis: { title: 'PC3', gridcolor: '#e5e7eb', zerolinecolor: '#d1d5db' },
            bgcolor: '#fafafa',
          },
          margin: { l: 0, r: 0, t: 40, b: 0 },
          paper_bgcolor: 'transparent',
          legend: { x: 0, y: 1, bgcolor: 'rgba(255,255,255,0.8)' },
          autosize: true,
        };

        const config = {
          responsive: true,
          displayModeBar: true,
          modeBarButtonsToRemove: ['toImage', 'sendDataToCloud'] as string[],
        };

        const container = plotlyContainerRef.current as HTMLDivElement & { _plotly_initialized?: boolean };
        if (container._plotly_initialized) {
          PlotlyLib.react(container, traces, layout, config);
        } else {
          PlotlyLib.newPlot(container, traces, layout, config);
          container._plotly_initialized = true;
        }
      } catch (err) {
        console.error('Plotly rendering error:', err);
      }
    };

    void renderPlotly();
  }, [embeddingMap, selectedProvider]);

  const fetchTableSchema = async (tableName: string) => {
    try {
      const response = await api.get<TableSchemaRaw>(`/master/database/schema/${tableName}`);
      if (response.data) {
        const d = response.data;
        setTableSchema({
          columns: (d.columns || []).map((c) => ({
            column_name: c.name,
            data_type: c.type,
            is_nullable: c.nullable ? 'YES' : 'NO',
            column_default: c.default_value ?? null,
            max_length: c.max_length ?? null
          })),
          primary_keys: d.primary_keys || [],
          foreign_keys: (d.foreign_keys || []).map((fk) => ({
            column_name: fk.column,
            foreign_table: fk.references_table,
            foreign_column: fk.references_column
          })),
          indexes: (d.indexes || []).map((idx) => ({
            index_name: idx.name,
            column_name: idx.columns?.join(', ') || '',
            is_unique: idx.is_unique ?? false
          }))
        });
      }
    } catch (error) {
      console.error('Error fetching schema:', error);
      toast({ title: 'Errore', description: 'Impossibile caricare lo schema', variant: 'destructive' });
    }
  };

  const fetchTableData = async (tableName: string, page: number = 1) => {
    try {
      const response = await api.get<TableDataResponse>(`/master/database/data/${tableName}?page=${page}&limit=${tableDataLimit}`);
      if (response.data) {
        setTableData(response.data.rows || []);
        setTableDataTotal(response.data.pagination?.total || 0);
        setTableDataPage(page);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Errore', description: 'Impossibile caricare i dati', variant: 'destructive' });
    }
  };

  const selectTable = async (tableName: string) => {
    setSelectedTable(tableName);
    setTableSchema(null);
    setTableData([]);
    await Promise.all([fetchTableSchema(tableName), fetchTableData(tableName)]);
  };

  const executeQuery = async () => {
    if (!sqlQuery.trim()) return;
    setQueryLoading(true);
    setQueryResult(null);
    setQueryError(null);
    setQueryColumns([]);

    try {
      const response = await api.post<{ rows: Record<string, unknown>[]; columns: string[] }>('/master/database/query', { query: sqlQuery });
      if (response.data?.rows) {
        setQueryResult(response.data.rows);
        setQueryColumns(response.data.columns || []);
      }
    } catch (error: unknown) {
      setQueryError(extractApiError(error, 'Errore nell\'esecuzione della query'));
    } finally {
      setQueryLoading(false);
    }
  };

  const saveRow = async () => {
    if (!selectedTable || !editingRow) return;
    try {
      const pkColumn = tableSchema?.primary_keys[0] || 'id';
      await api.post(`/master/database/data/${selectedTable}`, {
        primaryKey: { [pkColumn]: editingRow[pkColumn] },
        values: editingValues
      });
      toast({ title: 'Salvato', description: 'Riga aggiornata con successo' });
      setEditingRow(null);
      setEditingValues({});
      await fetchTableData(selectedTable, tableDataPage);
    } catch (error: unknown) {
      toast({ title: 'Errore', description: extractApiError(error, 'Errore nel salvataggio'), variant: 'destructive' });
    }
  };

  const deleteRow = async () => {
    if (!deleteConfirm) return;
    try {
      await api.delete(`/master/database/data/${deleteConfirm.table}/${deleteConfirm.id}`);
      toast({ title: 'Eliminato', description: 'Riga eliminata con successo' });
      setDeleteConfirm(null);
      if (selectedTable) await fetchTableData(selectedTable, tableDataPage);
    } catch (error: unknown) {
      toast({ title: 'Errore', description: extractApiError(error, 'Errore nell\'eliminazione'), variant: 'destructive' });
    }
  };

  const filteredTables = tables.filter(t => 
    t.table_name.toLowerCase().includes(searchTables.toLowerCase())
  );

  const topTables = [...tables].sort((a, b) => b.total_size_bytes - a.total_size_bytes).slice(0, 10);

  const pieData = topTables.map(t => ({
    name: t.table_name,
    value: t.total_size_bytes,
    displayValue: t.total_size
  }));

  const tenantBarData = tenantStorage.slice(0, 10).map(t => ({
    name: t.tenant_name || t.tenant_id.slice(0, 8),
    rows: t.total_rows
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground dark:text-white flex items-center gap-3">
            <Database className="h-8 w-8 text-purple-500" />
            Gestione Database
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">
            Monitora e gestisci il database PostgreSQL
          </p>
        </div>
        <Button onClick={fetchAll} variant="outline" className="gap-2">
          <RefreshCw className="h-4 w-4" />
          Aggiorna
        </Button>
      </div>

      <AnomaliesBanner onOpenRestore={openRestoreVerifications} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-8 lg:w-[1200px]">
          <TabsTrigger value="dashboard" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="ha" className="gap-2">
            <Zap className="h-4 w-4" />
            HA / Replica
          </TabsTrigger>
          <TabsTrigger value="observability" className="gap-2">
            <Activity className="h-4 w-4" />
            Observability
          </TabsTrigger>
          <TabsTrigger value="backup" className="gap-2">
            <Cloud className="h-4 w-4" />
            DR &amp; Backup
          </TabsTrigger>
          <TabsTrigger value="vectordb" className="gap-2" onClick={() => { if (!vectorStats) void fetchVectorStats(); }}>
            <Brain className="h-4 w-4" />
            Vector DB
          </TabsTrigger>
          <TabsTrigger value="schema" className="gap-2">
            <Table2 className="h-4 w-4" />
            Schema
          </TabsTrigger>
          <TabsTrigger value="data" className="gap-2">
            <HardDrive className="h-4 w-4" />
            Dati
          </TabsTrigger>
          <TabsTrigger value="query" className="gap-2">
            <Terminal className="h-4 w-4" />
            Query
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ha" className="space-y-6 mt-6">
          <HATab />
        </TabsContent>

        <TabsContent value="observability" className="space-y-6 mt-6">
          <ObservabilityTab onOpenRestore={openRestoreVerifications} />
        </TabsContent>

        <TabsContent value="backup" className="space-y-6 mt-6">
          <BackupTab restoreFocusRequest={restoreFocusRequest} />
          <RestorePanel />
        </TabsContent>

        <TabsContent value="dashboard" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70">Dimensione DB</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{stats?.database_size || '-'}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-cyan-500/10 to-cyan-600/5 border-cyan-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70">Tabelle</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-cyan-600 dark:text-cyan-400">{stats?.table_count || 0}</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70">Connessioni</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {stats?.active_connections || 0}
                  <span className="text-sm font-normal text-muted-foreground">/{stats?.max_connections || 100}</span>
                </p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70">Uptime</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">{stats?.uptime || '-'}</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-purple-500" />
                  Top 10 Tabelle per Dimensione
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RePieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name }) => {
                          const n = name ?? '';
                          return n.length > 12 ? n.slice(0, 12) + '...' : n;
                        }}
                      >
                        {pieData.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip 
                        formatter={(value) => formatBytes(chartNum(value))}
                        labelFormatter={(name) => `Tabella: ${name}`}
                      />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5 text-cyan-500" />
                  Storage per Tenant (Top 10)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={tenantBarData} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                      <XAxis type="number" />
                      <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value) => chartNum(value).toLocaleString() + ' righe'} />
                      <Bar dataKey="rows" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Table2 className="h-5 w-5 text-green-500" />
                Tutte le Tabelle
              </CardTitle>
              <CardDescription>
                {tables.length} tabelle nel database
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                <Input
                  placeholder="Cerca tabella..."
                  value={searchTables}
                  onChange={(e) => setSearchTables(e.target.value)}
                  className="pl-10"
                />
              </div>
              <ScrollArea className="h-[400px]">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Tabella</TableHead>
                      <TableHead className="text-right">Righe</TableHead>
                      <TableHead className="text-right">Dimensione</TableHead>
                      <TableHead className="text-right">Indici</TableHead>
                      <TableHead>Owner</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTables.map((table) => (
                      <TableRow 
                        key={table.table_name}
                        className="cursor-pointer hover:bg-muted dark:hover:bg-gray-800"
                        onClick={() => {
                          setActiveTab('schema');
                          void selectTable(table.table_name);
                        }}
                      >
                        <TableCell className="font-medium">{table.table_name}</TableCell>
                        <TableCell className="text-right">{table.row_count.toLocaleString()}</TableCell>
                        <TableCell className="text-right">{table.table_size}</TableCell>
                        <TableCell className="text-right">{table.index_size}</TableCell>
                        <TableCell>
                          <Badge variant={table.owner === 'edgadmin' ? 'default' : 'secondary'}>
                            {table.owner}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="vectordb" className="space-y-6 mt-6">
          {vectorLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
              <span className="ml-3 text-muted-foreground">Caricamento statistiche vettoriali...</span>
            </div>
          ) : vectorStats ? (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Panoramica del database vettoriale RAG — chunks, documenti, URL e embedding</p>
                <Button onClick={fetchVectorStats} variant="outline" size="sm" className="gap-2">
                  <RefreshCw className="h-3 w-3" />
                  Aggiorna
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 flex items-center gap-2">
                      <Hash className="h-4 w-4" /> Chunks Totali
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{vectorStats.totals.chunks.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 flex items-center gap-2">
                      <FileText className="h-4 w-4" /> Documenti
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{vectorStats.totals.documents.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 flex items-center gap-2">
                      <Globe className="h-4 w-4" /> URL Indicizzate
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{vectorStats.totals.urls.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 flex items-center gap-2">
                      <Zap className="h-4 w-4" /> Token Totali
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                      {vectorStats.totals.total_tokens >= 1000000
                        ? (vectorStats.totals.total_tokens / 1000000).toFixed(1) + 'M'
                        : vectorStats.totals.total_tokens >= 1000
                          ? (vectorStats.totals.total_tokens / 1000).toFixed(1) + 'K'
                          : vectorStats.totals.total_tokens.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">Media: {vectorStats.totals.avg_tokens} per chunk</p>
                  </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground dark:text-muted-foreground/70 flex items-center gap-2">
                      <Activity className="h-4 w-4" /> Embedding Coverage
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{vectorStats.totals.embedding_coverage}%</p>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                      <div className="bg-emerald-500 h-2 rounded-full transition-all" style={{ width: `${vectorStats.totals.embedding_coverage}%` }} />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Brain className="h-5 w-5 text-purple-500" />
                      Mappa Agenti
                    </CardTitle>
                    <CardDescription>Ogni punto rappresenta un agente — dimensione proporzionale ai token totali</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {vectorStats.per_agent.length === 0 ? (
                      <div className="flex items-center justify-center h-[350px] text-muted-foreground/70">
                        <p>Nessun agente con knowledge base</p>
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={350}>
                        <ScatterChart margin={{ top: 20, right: 30, bottom: 20, left: 20 }}>
                          <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                          <XAxis
                            type="number"
                            dataKey="chunk_count"
                            name="Chunks"
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Chunks', position: 'insideBottom', offset: -10, fontSize: 12 }}
                            domain={[0, (dataMax: number) => Math.max(dataMax * 1.2, 10)]}
                          />
                          <YAxis
                            type="number"
                            dataKey="avg_tokens"
                            name="Token medi"
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Token medi', angle: -90, position: 'insideLeft', fontSize: 12 }}
                            domain={[0, (dataMax: number) => Math.max(dataMax * 1.2, 100)]}
                          />
                          <ZAxis type="number" dataKey="total_tokens" range={[120, 800]} name="Token totali" />
                          <Tooltip
                            cursor={{ strokeDasharray: '3 3' }}
                            content={({ payload }) => {
                              if (!payload || !payload.length) return null;
                              const d = payload[0]?.payload;
                              if (!d) return null;
                              return (
                                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border text-sm">
                                  <p className="font-semibold text-foreground dark:text-white">{d.agent_name}</p>
                                  <p className="text-muted-foreground text-xs">{d.tenant_name} · {d.agent_type}</p>
                                  <div className="mt-2 space-y-1">
                                    <p className="text-foreground dark:text-muted-foreground/60">Chunks: <span className="font-medium">{d.chunk_count}</span></p>
                                    <p className="text-foreground dark:text-muted-foreground/60">Documenti: <span className="font-medium">{d.doc_count}</span></p>
                                    <p className="text-foreground dark:text-muted-foreground/60">URL: <span className="font-medium">{d.url_count}</span></p>
                                    <p className="text-foreground dark:text-muted-foreground/60">Token medi: <span className="font-medium">{d.avg_tokens}</span></p>
                                    <p className="text-foreground dark:text-muted-foreground/60">Token totali: <span className="font-medium">{d.total_tokens?.toLocaleString()}</span></p>
                                  </div>
                                </div>
                              );
                            }}
                          />
                          {['voice', 'chatbot', 'elevenlabs', 'other'].map(type => {
                            const data = vectorStats.per_agent.filter(a => a.agent_type === type);
                            if (!data.length) return null;
                            return (
                              <Scatter
                                key={type}
                                name={type.charAt(0).toUpperCase() + type.slice(1)}
                                data={data}
                                fill={AGENT_TYPE_COLORS[type]}
                                fillOpacity={0.8}
                                shape={(props: ScatterShapeProps) => {
                                  const { cx, cy, fill } = props;
                                  const r = Math.max(6, Math.min(20, (props.payload?.total_tokens || 1000) / 3000));
                                  return <circle cx={cx} cy={cy} r={r} fill={fill} fillOpacity={0.8} stroke={fill} strokeWidth={2} strokeOpacity={0.4} />;
                                }}
                              />
                            );
                          })}
                          <Legend />
                        </ScatterChart>
                      </ResponsiveContainer>
                    )}
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="h-5 w-5 text-blue-500" />
                      Chunks per Tenant
                    </CardTitle>
                    <CardDescription>Top 15 tenant per numero di chunks</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={350}>
                      <BarChart data={vectorStats.per_tenant.slice(0, 15)} margin={{ top: 5, right: 30, left: 10, bottom: 60 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis dataKey="tenant_name" tick={{ fontSize: 10, angle: -45, textAnchor: 'end' }} interval={0} height={80} />
                        <YAxis tick={{ fontSize: 12 }} />
                        <Tooltip
                          content={({ payload, label }) => {
                            if (!payload || !payload.length) return null;
                            const d = payload[0]?.payload;
                              if (!d) return null;
                            return (
                              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border text-sm">
                                <p className="font-semibold text-foreground dark:text-white">{label}</p>
                                <p className="text-foreground dark:text-muted-foreground/60">Chunks: <span className="font-medium">{d.chunk_count?.toLocaleString()}</span></p>
                                <p className="text-foreground dark:text-muted-foreground/60">Documenti: <span className="font-medium">{d.doc_count}</span></p>
                                <p className="text-foreground dark:text-muted-foreground/60">URL: <span className="font-medium">{d.url_count}</span></p>
                                <p className="text-foreground dark:text-muted-foreground/60">Token: <span className="font-medium">{d.total_tokens?.toLocaleString()}</span></p>
                              </div>
                            );
                          }}
                        />
                        <Bar dataKey="chunk_count" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Chunks" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <PieChart className="h-5 w-5 text-green-500" />
                      Source Type
                    </CardTitle>
                    <CardDescription>Distribuzione chunks per origine</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <RePieChart>
                        <Pie
                          data={vectorStats.source_types}
                          dataKey="count"
                          nameKey="source_type"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          label={({ source_type, percent }: { source_type?: string; percent?: number }) => `${source_type ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`}
                          labelLine={{ strokeWidth: 1 }}
                        >
                          {vectorStats.source_types.map((entry, index) => (
                            <Cell key={entry.source_type} fill={SOURCE_TYPE_COLORS[entry.source_type] || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value) => chartNum(value).toLocaleString()} />
                      </RePieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="h-5 w-5 text-cyan-500" />
                      Chunks Creati — Ultimi 30 Giorni
                    </CardTitle>
                    <CardDescription>Timeline di indicizzazione</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={250}>
                      <AreaChart
                        data={(() => {
                          const timelineMap = new Map<string, number>();
                          (vectorStats.timeline || []).forEach((t) => {
                            timelineMap.set(t.day, Number(t.count) || 0);
                          });
                          const days: { day: string; count: number }[] = [];
                          const now = new Date();
                          for (let i = 29; i >= 0; i--) {
                            const d = new Date(now);
                            d.setDate(d.getDate() - i);
                            const key = d.toISOString().split('T')[0] ?? '';
                            days.push({ day: key, count: timelineMap.get(key) || 0 });
                          }
                          return days;
                        })()}
                        margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                        <XAxis
                          dataKey="day"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(val) => {
                            const d = new Date(chartLabel(val));
                            return `${d.getDate()}/${d.getMonth() + 1}`;
                          }}
                        />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                        <Tooltip
                          labelFormatter={(val) => {
                            const d = new Date(chartLabel(val));
                            return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
                          }}
                          formatter={(value) => [chartNum(value).toLocaleString(), 'Chunks']}
                        />
                        <Area
                          type="monotone"
                          dataKey="count"
                          stroke="#06b6d4"
                          fill="#06b6d4"
                          fillOpacity={0.2}
                          strokeWidth={2}
                          dot={{ r: 3, fill: '#06b6d4', strokeWidth: 0 }}
                          activeDot={{ r: 5, fill: '#06b6d4', stroke: '#fff', strokeWidth: 2 }}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Table2 className="h-5 w-5 text-indigo-500" />
                        Dettaglio Agenti
                      </CardTitle>
                      <CardDescription>Tutti gli agenti con knowledge base attiva</CardDescription>
                    </div>
                    <Input
                      placeholder="Cerca agente o tenant..."
                      value={vectorSearch}
                      onChange={(e) => setVectorSearch(e.target.value)}
                      className="w-64"
                    />
                  </div>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Tenant</TableHead>
                          <TableHead>Agente</TableHead>
                          <TableHead>Tipo</TableHead>
                          <TableHead className="text-right">Chunks</TableHead>
                          <TableHead className="text-right">Documenti</TableHead>
                          <TableHead className="text-right">URL</TableHead>
                          <TableHead className="text-right">Token Totali</TableHead>
                          <TableHead className="text-right">Token Medi</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {vectorStats.per_agent
                          .filter(a => {
                            if (!vectorSearch) return true;
                            const s = vectorSearch.toLowerCase();
                            return a.agent_name.toLowerCase().includes(s) || a.tenant_name.toLowerCase().includes(s) || a.agent_type.toLowerCase().includes(s);
                          })
                          .map((agent) => (
                            <TableRow key={`${agent.agent_id}-${agent.tenant_id}`}>
                              <TableCell className="text-sm">{agent.tenant_name}</TableCell>
                              <TableCell className="text-sm font-medium">{agent.agent_name}</TableCell>
                              <TableCell>
                                <Badge variant="outline" style={{ borderColor: AGENT_TYPE_COLORS[agent.agent_type], color: AGENT_TYPE_COLORS[agent.agent_type] }}>
                                  {agent.agent_type}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right font-mono">{agent.chunk_count.toLocaleString()}</TableCell>
                              <TableCell className="text-right font-mono">{agent.doc_count}</TableCell>
                              <TableCell className="text-right font-mono">{agent.url_count}</TableCell>
                              <TableCell className="text-right font-mono">{agent.total_tokens.toLocaleString()}</TableCell>
                              <TableCell className="text-right font-mono">{agent.avg_tokens}</TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Orbit className="h-5 w-5 text-violet-500 shrink-0" />
                        Embedding Space 3D
                      </CardTitle>
                      <CardDescription className="mt-1">
                        PCA 3D per-provider — spazi vettoriali OpenAI (1536D) e Gemini (3072D) separati
                      </CardDescription>
                      {embeddingMap && embeddingMap.providers.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {embeddingMap.providers.map(pg => (
                            <span
                              key={pg.provider}
                              className={`inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border font-medium ${
                                pg.provider === 'openai'
                                  ? 'bg-green-50 dark:bg-green-900/20 border-green-300 dark:border-green-700 text-green-700 dark:text-green-400'
                                  : 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-400'
                              }`}
                            >
                              {pg.provider === 'openai' ? 'OpenAI' : 'Gemini'}
                              {' · '}
                              {pg.stats.with_embedding} pt
                              {pg.stats.total_in_db > 0 && ` / ${pg.stats.total_in_db} tot`}
                              {' · '}
                              {pg.dimensions}D
                              {pg.error && (
                                <AlertTriangle className="h-3 w-3 text-amber-500 ml-1" />
                              )}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {embeddingMap && embeddingMap.providers.length > 1 && (
                        <div className="flex rounded-md border overflow-hidden">
                          {embeddingMap.providers.map(pg => (
                            <button
                              key={pg.provider}
                              onClick={() => setSelectedProvider(pg.provider)}
                              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                                selectedProvider === pg.provider
                                  ? 'bg-violet-600 text-white'
                                  : 'bg-white dark:bg-gray-800 text-muted-foreground hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                            >
                              {pg.provider === 'openai' ? 'OpenAI' : 'Gemini'}
                            </button>
                          ))}
                        </div>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={fetchEmbeddingMap}
                        disabled={embeddingLoading}
                      >
                        {embeddingLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Orbit className="h-4 w-4" />}
                        <span className="ml-2">{embeddingMap ? 'Aggiorna' : 'Carica Mappa 3D'}</span>
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  {embeddingError ? (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground/70">
                      <AlertTriangle className="h-10 w-10 mb-3 text-amber-500 opacity-70" />
                      <p className="text-sm font-medium text-amber-600 dark:text-amber-400">{embeddingError}</p>
                      <Button variant="ghost" size="sm" className="mt-3" onClick={fetchEmbeddingMap}>
                        Riprova
                      </Button>
                    </div>
                  ) : !embeddingMap ? (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground/70">
                      <Orbit className="h-10 w-10 mb-3 opacity-30" />
                      <p className="text-sm">Clicca "Carica Mappa 3D" per visualizzare lo spazio vettoriale</p>
                    </div>
                  ) : embeddingMap.providers.length === 0 || embeddingMap.total_in_db === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-muted-foreground/70">
                      <Info className="h-10 w-10 mb-3 opacity-30" />
                      <p className="text-sm font-medium">Nessun embedding nel database</p>
                      <p className="text-xs mt-1 opacity-70">Gli embedding appariranno dopo il primo ingest di documenti</p>
                    </div>
                  ) : (() => {
                    const activeGroup = embeddingMap.providers.find(p => p.provider === selectedProvider);
                    if (!activeGroup) return null;
                    if (activeGroup.error) {
                      return (
                        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground/70">
                          <AlertTriangle className="h-10 w-10 mb-3 text-amber-500 opacity-70" />
                          <p className="text-sm font-medium text-amber-600 dark:text-amber-400">{activeGroup.error}</p>
                          <p className="text-xs mt-1 opacity-70">
                            {activeGroup.stats.with_embedding} embedding campionati su {activeGroup.stats.total_in_db} totali ({activeGroup.dimensions}D)
                          </p>
                        </div>
                      );
                    }
                    if (activeGroup.points.length === 0) {
                      return (
                        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground/70">
                          <Info className="h-10 w-10 mb-3 opacity-30" />
                          <p className="text-sm font-medium">
                            Nessun embedding valido per {activeGroup.provider === 'openai' ? 'OpenAI' : 'Gemini'}
                          </p>
                          <p className="text-xs mt-1 opacity-70">
                            {activeGroup.stats.total_in_db} chunks nel DB — nessun vettore campionato correttamente
                          </p>
                        </div>
                      );
                    }
                    return <div ref={plotlyContainerRef} style={{ width: '100%', height: '500px' }} />;
                  })()}
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
              <Brain className="h-12 w-12 mb-4 opacity-30" />
              <p>Clicca il tab per caricare le statistiche del database vettoriale</p>
            </div>
          )}
        </TabsContent>

        <TabsContent value="schema" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <Card className="lg:col-span-1">
              <CardHeader>
                <CardTitle className="text-lg">Tabelle</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="p-4 border-b">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground/70" />
                    <Input
                      placeholder="Cerca..."
                      value={searchTables}
                      onChange={(e) => setSearchTables(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <ScrollArea className="h-[500px]">
                  {filteredTables.map((table) => (
                    <button
                      key={table.table_name}
                      onClick={() => selectTable(table.table_name)}
                      className={`w-full text-left px-4 py-3 border-b hover:bg-muted dark:hover:bg-gray-800 transition-colors ${
                        selectedTable === table.table_name ? 'bg-purple-100 dark:bg-purple-900/30 border-l-2 border-l-purple-500' : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{table.table_name}</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground/70" />
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {table.row_count.toLocaleString()} righe • {table.total_size}
                      </div>
                    </button>
                  ))}
                </ScrollArea>
              </CardContent>
            </Card>

            <Card className="lg:col-span-3">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  {selectedTable ? (
                    <>
                      <Table2 className="h-5 w-5 text-purple-500" />
                      Schema: {selectedTable}
                    </>
                  ) : (
                    <>
                      <Info className="h-5 w-5 text-muted-foreground/70" />
                      Seleziona una tabella
                    </>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {tableSchema ? (
                  <div className="space-y-6">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Table2 className="h-4 w-4" />
                        Colonne ({tableSchema.columns.length})
                      </h4>
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Nome</TableHead>
                            <TableHead>Tipo</TableHead>
                            <TableHead>Nullable</TableHead>
                            <TableHead>Default</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tableSchema.columns.map((col) => (
                            <TableRow key={col.column_name}>
                              <TableCell className="font-medium">
                                {col.column_name}
                                {tableSchema.primary_keys.includes(col.column_name) && (
                                  <Badge variant="outline" className="ml-2 text-[10px]">PK</Badge>
                                )}
                              </TableCell>
                              <TableCell>
                                <code className="text-xs bg-muted dark:bg-gray-800 px-2 py-1 rounded">
                                  {col.data_type}{col.max_length ? `(${col.max_length})` : ''}
                                </code>
                              </TableCell>
                              <TableCell>
                                {col.is_nullable === 'YES' ? (
                                  <Badge variant="secondary">Nullable</Badge>
                                ) : (
                                  <Badge variant="default">NOT NULL</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                                {col.column_default || '-'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>

                    {tableSchema.foreign_keys.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3">Foreign Keys</h4>
                        <div className="space-y-2">
                          {tableSchema.foreign_keys.map((fk, i) => (
                            <div key={i} className="flex items-center gap-2 text-sm">
                              <Badge variant="outline">{fk.column_name}</Badge>
                              <span className="text-muted-foreground/70">→</span>
                              <Badge variant="secondary">{fk.foreign_table}.{fk.foreign_column}</Badge>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {tableSchema.indexes.length > 0 && (
                      <div>
                        <h4 className="font-semibold mb-3">Indici</h4>
                        <div className="flex flex-wrap gap-2">
                          {tableSchema.indexes.map((idx, i) => (
                            <Badge key={i} variant={idx.is_unique ? 'default' : 'secondary'}>
                              {idx.index_name} ({idx.column_name})
                              {idx.is_unique && ' - UNIQUE'}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Table2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Seleziona una tabella per visualizzare lo schema</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="data" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <HardDrive className="h-5 w-5 text-purple-500" />
                  {selectedTable ? `Dati: ${selectedTable}` : 'Seleziona una tabella'}
                </CardTitle>
                {selectedTable && (
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => fetchTableData(selectedTable, tableDataPage)}
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Aggiorna
                    </Button>
                  </div>
                )}
              </div>
              {selectedTable && (
                <CardDescription>
                  Totale: {tableDataTotal.toLocaleString()} righe • Pagina {tableDataPage} di {Math.ceil(tableDataTotal / tableDataLimit)}
                </CardDescription>
              )}
            </CardHeader>
            <CardContent>
              {!selectedTable ? (
                <div className="text-center py-12">
                  <HardDrive className="h-12 w-12 mx-auto mb-4 opacity-50 text-muted-foreground/70" />
                  <p className="text-muted-foreground">Seleziona una tabella dal tab Schema</p>
                </div>
              ) : tableData.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Info className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun dato nella tabella</p>
                </div>
              ) : (
                <>
                  <ScrollArea className="h-[500px]">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {Object.keys(tableData[0] ?? {}).map((key) => (
                            <TableHead key={key} className="whitespace-nowrap">
                              {key}
                            </TableHead>
                          ))}
                          <TableHead className="w-[100px]">Azioni</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tableData.map((row, idx) => {
                          const isEditing = editingRow && JSON.stringify(editingRow) === JSON.stringify(row);
                          return (
                            <TableRow key={idx}>
                              {Object.entries(row).map(([key, value]) => (
                                <TableCell key={key} className="max-w-[200px]">
                                  {isEditing ? (
                                    <Input
                                      value={String(editingValues[key] ?? value ?? '')}
                                      onChange={(e) => setEditingValues({ ...editingValues, [key]: e.target.value })}
                                      className="h-8 text-xs"
                                    />
                                  ) : (
                                    <span className="text-xs truncate block">
                                      {value === null ? <span className="text-muted-foreground/70 italic">NULL</span> : String(value)}
                                    </span>
                                  )}
                                </TableCell>
                              ))}
                              <TableCell>
                                {isEditing ? (
                                  <div className="flex gap-1">
                                    <Button size="sm" variant="ghost" onClick={saveRow}>
                                      <Save className="h-4 w-4 text-green-500" />
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => { setEditingRow(null); setEditingValues({}); }}>
                                      <X className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="flex gap-1">
                                    <Button size="sm" variant="ghost" onClick={() => { setEditingRow(row); setEditingValues(row); }}>
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                    <Button 
                                      size="sm" 
                                      variant="ghost" 
                                      onClick={() => {
                                        const firstKey = Object.keys(row)[0];
                                        setDeleteConfirm({ table: selectedTable, id: String(row.id ?? (firstKey ? row[firstKey] : '') ?? '') });
                                      }}
                                    >
                                      <Trash2 className="h-4 w-4 text-red-500" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </ScrollArea>

                  <div className="flex items-center justify-between mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={tableDataPage <= 1}
                      onClick={() => fetchTableData(selectedTable, tableDataPage - 1)}
                    >
                      Precedente
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Pagina {tableDataPage} di {Math.ceil(tableDataTotal / tableDataLimit)}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={tableDataPage >= Math.ceil(tableDataTotal / tableDataLimit)}
                      onClick={() => fetchTableData(selectedTable, tableDataPage + 1)}
                    >
                      Successiva
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="query" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Terminal className="h-5 w-5 text-purple-500" />
                Console SQL
              </CardTitle>
              <CardDescription>
                Esegui query SELECT per interrogare il database
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={sqlQuery}
                  onChange={(e) => setSqlQuery(e.target.value)}
                  placeholder="SELECT * FROM tabella LIMIT 100;"
                  className="font-mono text-sm min-h-[120px]"
                />
                <div className="flex items-center gap-2">
                  <Button onClick={executeQuery} disabled={queryLoading} className="gap-2">
                    {queryLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                    Esegui
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigator.clipboard.writeText(sqlQuery)}
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copia
                  </Button>
                </div>
              </div>

              {queryError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-red-700 dark:text-red-400">Errore</h4>
                      <p className="text-sm text-red-600 dark:text-red-300 mt-1 font-mono">{queryError}</p>
                    </div>
                  </div>
                </div>
              )}

              {queryResult && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 inline mr-2 text-green-500" />
                      {queryResult.length} righe restituite
                    </p>
                  </div>
                  <ScrollArea className="h-[400px] border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          {queryColumns.map((col) => (
                            <TableHead key={col} className="whitespace-nowrap">{col}</TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {queryResult.map((row, idx) => (
                          <TableRow key={idx}>
                            {queryColumns.map((col) => (
                              <TableCell key={col} className="text-xs max-w-[200px] truncate">
                                {row[col] === null ? <span className="text-muted-foreground/70 italic">NULL</span> : String(row[col])}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              Conferma Eliminazione
            </DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questa riga? L'azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Annulla</Button>
            <Button variant="destructive" onClick={deleteRow}>Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
